// src/components/Doctor/DoctorAppointments.jsx
import React, { useEffect, useState } from "react";
import { Table, Button, Tag, Space, Modal, Typography, Input } from "antd";
import { VideoCameraOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  cancelAppointment,
  fetchTodayAppointments,
  rescheduleAppointment,
  updateAppointmentStatus,
} from "../../Redux/Slice/Doctor/Appointments";
import { toast } from "react-toastify";
import { fetchDoctorAvailability } from "../../Redux/Slice/Doctor/Slots";
import AppointmentModal from "./AppointmentModal";
// import { connectWebSocket, disconnectWebSocket } from "../../websocket";

const { TextArea } = Input;

const DoctorAppointments = ({ filterStatus, setTrigger }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [appointments, setAppointments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const [rejectReason, setRejectReason] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const [loadingId, setLoadingId] = useState(null);

  const [currentTime, setCurrentTime] = useState(dayjs());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(dayjs());
    }, 10000); // ⏱ every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const user = JSON.parse(localStorage.getItem("user"));
  const doctorId = user?.doctorId;

  const { data, loading } = useSelector(
    (state) => state.appointment.todayAppointments
  );

  const {
    data: updateData,
    error: updateError,
    loading: updateLoading,
  } = useSelector((state) => state.appointment.updateStatus);

  const { data: slotData, loading: slotLoading } = useSelector(
    (state) => state.slot.availableSlot
  );

  const {
    data: rescheduleData,
    error: rescheduleError,
    loading: rescheduleLoading,
  } = useSelector((state) => state.appointment.reschedule);

  const {
    data: cancelData,
    error: cancelError,
    loading: cancelLoading,
  } = useSelector((state) => state.appointment.cancel);

  const { data: notifyData } = useSelector(
    (state) => state?.call?.notification
  );
  const calls = notifyData?.createdVideoCalls || [];

  useEffect(() => {
    if (data?.status) {
      setAppointments(data.data || []);
    }
  }, [data]);

  useEffect(() => {
    // Initial fetch
    dispatch(fetchTodayAppointments(doctorId));

    // Connect WebSocket
    // connectWebSocket(doctorId, (newAppointment) => {
    //   console.log("🔔 New appointment received:", newAppointment);
    //   dispatch(fetchTodayAppointments(doctorId)); // refresh appointments
    // });

    // Cleanup
    // return () => disconnectWebSocket();
  }, [doctorId, dispatch]);

  const handleStatusChange = async (id, status) => {
    try {
      setLoadingId(id);
      await dispatch(updateAppointmentStatus({ appointmentId: id, status }));
    } finally {
      setLoadingId(null);
      setTrigger((prev) => !prev);
    }
  };

  useEffect(() => {
    if (updateData?.status) {
      toast.success(updateData.message || "Status updated successfully!");
      dispatch(fetchTodayAppointments(doctorId));
      setIsRejectModalOpen(false);
      setSelectedAppointment(null);
    } else if (updateError) {
      toast.error(updateError.message || "Failed to update status");
    }
  }, [updateData, updateError, dispatch, doctorId]);

  useEffect(() => {
    if (rescheduleData?.status) {
      toast.success(rescheduleData.message || "Rescheduled successfully!");
      dispatch(fetchTodayAppointments(doctorId));
      setIsModalOpen(false);
      setSelectedAppointment(null);
    }
    if (rescheduleError) {
      toast.error(rescheduleError.message || "Failed to reschedule");
    }
  }, [rescheduleData, rescheduleError, dispatch, doctorId]);

  useEffect(() => {
    if (cancelData?.status) {
      toast.success(cancelData.message || "Cancelled successfully!");
      dispatch(fetchTodayAppointments(doctorId));
      setIsCancelModalOpen(false);
      setSelectedAppointment(null);
    }
    if (cancelError) {
      toast.error(cancelError.message || "Failed to cancel");
    }
  }, [cancelData, cancelError, dispatch, doctorId]);

  const handleBookOrReschedule = (values) => {
    if (modalType === "reschedule" && selectedAppointment) {
      dispatch(
        rescheduleAppointment({
          appointmentDate: values.appointmentDate.format("YYYY-MM-DD"),
          appointmentTime: values.appointmentTime,
          appointmentId: selectedAppointment.appointmentId,
          doctorId,
        })
      );
    }
  };

  useEffect(() => {
    if (data?.status) {
      let filtered = data.data || [];
      if (filterStatus && filterStatus !== "Total") {
        filtered = filtered.filter(
          (appt) =>
            (appt.confirmStatus === "Unconfirmed" &&
              filterStatus === "Pending") ||
            appt.confirmStatus === filterStatus
        );
      }
      setAppointments(filtered);
    }
  }, [data, filterStatus]);

  // ✅ Helper to get call status from notification
  const getCallStatus = (appointmentId) => {
    const call = calls.find((c) => c.appointmentId === appointmentId);
    return call ? call.status : null; // waiting | active | ended | auto-ended
  };

  const columns = [
    {
      title: "Sl No",
      key: "slNo",
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "Appointment Date & Time",
      key: "appointmentDateTime",
      render: (record) =>
        `${dayjs(record.appointmentDate).format("YYYY-MM-DD")} ${
          record.appointmentTime
        }`,
    },
    {
      title: "Patient",
      dataIndex: "patientName",
      key: "patientName",
    },
    {
      title: "Status",
      dataIndex: "confirmStatus",
      key: "confirmStatus",
      render: (status) => {
        const colors = {
          Pending: "orange",
          Confirmed: "green",
          Rejected: "red",
          Cancelled: "volcano",
          Ended: "gray",
        };
        const displayStatus = status === "Unconfirmed" ? "Pending" : status;
        return (
          <Tag color={colors[displayStatus]}>
            {displayStatus?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const callStatus = getCallStatus(record.appointmentId);

        // ✅ Priority 1: Video Call status overrides everything
        if (callStatus === "Waiting" || callStatus === "Active") {
          return (
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              onClick={() =>
                navigate("/video-call", {
                  state: {
                    patientId: record.patientId,
                    appointmentId: record.appointmentId,
                  },
                })
              }
            >
              Start Call
            </Button>
          );
        }
        if (callStatus === "Ended" || callStatus === "AutoEnded") {
          return (
            <Button disabled type="default">
              Call Ended
            </Button>
          );
        }

        // ✅ Priority 2: Appointment status
        if (record.confirmStatus === "Unconfirmed") {
          return (
            <Space>
              <Button
                type="primary"
                onClick={() =>
                  handleStatusChange(record.appointmentId, "Confirmed")
                }
                loading={loadingId === record.appointmentId}
              >
                Accept
              </Button>
              <Button
                danger
                onClick={() => {
                  setIsRejectModalOpen(true);
                  setSelectedAppointment(record);
                }}
              >
                Reject
              </Button>
            </Space>
          );
        }

        if (record.confirmStatus === "Confirmed") {
          // ✅ Compare current time with appointment time
          const appointmentDateTime = dayjs(
            `${record.appointmentDate} ${record.appointmentTime}`,
            "YYYY-MM-DD HH:mm"
          );

          const isTimeReached =
            currentTime.isAfter(appointmentDateTime) ||
            currentTime.isSame(appointmentDateTime);

          return (
            <Space>
              <Button
                onClick={() => {
                  setSelectedAppointment(record);
                  setModalType("reschedule");
                  setIsModalOpen(true);
                }}
              >
                Reschedule
              </Button>
              <Button
                danger
                onClick={() => {
                  setIsCancelModalOpen(true);
                  setSelectedAppointment(record);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                disabled={!isTimeReached} // ✅ disabled before appointment time
                onClick={() =>
                  navigate("/video-call", {
                    state: {
                      patientId: record.patientId,
                      appointmentId: record.appointmentId,
                    },
                  })
                }
              >
                Start Call
              </Button>
            </Space>
          );
        }
      },
    },
  ];

  return (
    <div>
      <Space
        style={{
          marginBottom: 16,
          justifyContent: "space-between",
          width: "100%",
          alignItems: "center",
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: "#5d5e5dff" }}>
            Today Appointments
          </Typography.Title>
        </div>
      </Space>

      <Table
        columns={columns}
        dataSource={appointments}
        rowKey={(record) => record.appointmentId}
        pagination={{ pageSize: 5 }}
        loading={loading}
      />

      {/* Appointment Modal */}
      <AppointmentModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        modalType={modalType}
        handleBookOrReschedule={handleBookOrReschedule}
        doctorId={doctorId}
        dispatch={dispatch}
        fetchDoctorAvailability={fetchDoctorAvailability}
        slotData={slotData}
        slotLoading={slotLoading}
        selectedAppointment={selectedAppointment}
        rescheduleLoading={rescheduleLoading}
      />

      {/* Reject Modal */}
      <Modal
        title="Reject Appointment"
        open={isRejectModalOpen}
        onCancel={() => setIsRejectModalOpen(false)}
        footer={null}
        width={500}
        style={{ top: 20 }}
        styles={{ body: { padding: "20px" } }}
      >
        <Typography.Text strong>
          Please provide a reason for rejecting this appointment:
        </Typography.Text>

        <TextArea
          rows={4}
          placeholder="Enter rejection reason..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          style={{ marginTop: 12 }}
        />

        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Button
            onClick={() => setIsRejectModalOpen(false)}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => {
              dispatch(
                updateAppointmentStatus({
                  appointmentId: selectedAppointment?.appointmentId,
                  status: "Rejected",
                  rejectReason,
                })
              ).then(() => {
                setTrigger((prev) => !prev);
                setIsRejectModalOpen(false);
                setRejectReason("");
              });
            }}
            loading={updateLoading}
            disabled={!rejectReason?.trim()}
          >
            Confirm
          </Button>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        title="Cancel Appointment"
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        footer={null}
        width={500}
        style={{ top: 20 }}
        styles={{ body: { padding: "20px" } }}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter cancellation reason"
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
        />

        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Button
            onClick={() => setIsCancelModalOpen(false)}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => {
              dispatch(
                cancelAppointment({
                  appointmentId: selectedAppointment?.appointmentId,
                  cancellationReason,
                })
              ).then(() => setTrigger((prev) => !prev));
            }}
            loading={cancelLoading}
          >
            Confirm
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DoctorAppointments;
